import { Card, CardBody } from '@heroui/react';
import React, { useEffect, useRef } from 'react';
import { FiWifiOff } from 'react-icons/fi';

import { useNotification } from '../../contexts/NotificationContext';
import { useIsOnline } from '../../hooks/useIsOnline';

/**
 * Connectivity feedback for the admin shell, in two steps — the same pair the
 * legacy admin header had, so nobody loses a signal they relied on.
 *
 * While the connection is down a banner sits at the top of the page and cannot
 * be dismissed: without it the portal fails silently, since a dropped request
 * only reaches a `console.error` in the Apollo client. When a network comes
 * back the banner goes and a short note carries the one instruction its
 * absence cannot: work attempted while offline needs retrying. The note stops
 * short of saying the connection is back, because nothing here has reached the
 * backend to find out.
 *
 * Must be rendered inside `NotificationProvider`.
 */
export const OfflineBanner: React.FC = () => {
  const isOnline = useIsOnline();
  const { addNotification } = useNotification();
  const hasBeenOffline = useRef(false);

  useEffect(() => {
    if (!isOnline) {
      hasBeenOffline.current = true;
      return;
    }

    // Only confirm a recovery, never a first render that was online all along.
    if (hasBeenOffline.current) {
      hasBeenOffline.current = false;
      addNotification({
        // Not "Connection restored", and not a success. The browser's `online`
        // event only says a network interface came up: behind a captive portal
        // or with the VPN still down, Choco stays unreachable. Nothing here has
        // reached the backend, so announcing recovery would send someone
        // straight into another silent failed save. The retry is the check.
        title: 'Connection detected',
        // Just the instruction. A retry can fail for reasons that have nothing
        // to do with the network, and the screen making the request is the one
        // that knows which. Guessing here would send someone to check their
        // wifi over a rejected input.
        message: "Retry anything that didn't save.",
        type: 'info',
      });
    }
  }, [isOnline, addNotification]);

  if (isOnline) return null;

  return (
    <Card
      role="alert"
      aria-live="assertive"
      shadow="none"
      // Sticky on purpose. The banner sits inside the scrolling content area,
      // and a connectivity warning that scrolls out of view is no warning at
      // all: someone deep in a long list is exactly who needs to know their
      // next save will not land.
      className="sticky top-0 z-20 w-full mb-6 rounded-lg bg-danger-50 border border-danger"
    >
      <CardBody className="flex flex-row items-start gap-3 p-4">
        <FiWifiOff
          size={20}
          className="text-danger flex-shrink-0 mt-0.5"
          aria-hidden="true"
        />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">
            No internet connection
          </p>
          <p className="text-sm text-default-700">
            Reconnect to carry on. Changes you make now won&apos;t save.
          </p>
        </div>
      </CardBody>
    </Card>
  );
};
